import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, PopoverPosition, TableStyles_Types } from "../../../../Definitions";
import { colors, fonts, frames, glyphs } from "../../../../Styles";
import NewButton from "../../../../Components/BaseComponents/newButton";
import { NewInput, PageHeader, PrimaryPopover } from "../../../../Components";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel } from "@tanstack/react-table";
import { GenericDataParameters, GenericRow, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import { LoadingSpinner } from "../../../../Services/wrapper";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";
import { debounce } from "lodash";
import { useLazyQuery } from "@apollo/client";
import { ExtendedCompaniesResponse, ExtendedCompaniesArgs, EXTENDED_COMPANIES } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/extendedCompanies";

const columnsData: GenericDataParameters<GenericRow>[] = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "domain", accessor: "domain", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "name", accessor: "name", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "tags", accessor: "tags", type: ListData_ValueTypes.Div, filter: false },
	{ headerValue: "pin", accessor: "pin", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "vpn address", accessor: "vpn_address", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "city", accessor: "city", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "street", accessor: "street", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "phone", accessor: "phone", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "vat", accessor: "vat", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "email", accessor: "email", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "legacy", accessor: "legacy", type: ListData_ValueTypes.Check, sort: true },
]

export default function Companies() {
	const limit = 50;

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: limit,
	});

	const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

	const [fetchData, { data: data, loading: loading, error: error, }] = useLazyQuery<ExtendedCompaniesResponse, ExtendedCompaniesArgs>(EXTENDED_COMPANIES, {
		fetchPolicy: "network-only"
	});

	useEffect(() => {
		setPagination({ pageIndex: 0, pageSize: limit });
		fetchData();
	}, []);

	const tableData: GenericRow[] = useMemo(() => {
		const companies = data?.user.extendedCompanies.map(company => {
			return {
				...company,
				id: company.company_id,
				tags: company.tags && (
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<PrimaryPopover
							content={
								<div style={{ width: "fit-content", height: "fit-content", margin: "-5px 0px", cursor: "pointer" }}>
									<glyphs.Note fillColor={colors.greyDarkPrimary} />
								</div>
							}
							overrides={{
								popoverPosition: PopoverPosition.bottom
							}}
							popoverHeader={"Tags"}
							popoverContent={
								<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", color: colors.whitePrimary }}>
									{company.tags.map((tag, i) => <p key={i} >{tag}</p>)}
								</div>
							}
						/>
					</div>
				)
			}
		});
		return companies ?? [];
	}, [data]);

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onPaginationChange: setPagination,
		globalFilterFn: globalFilter,
		state: {
			globalFilter: globalFilterValue,
			pagination,
		},
	});

	const debouncedSearch = useCallback(
		debounce((value: string) => {
			setGlobalFilterValue(value);
		}, 500),
		[]
	);

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Companies' subHeader='Overview' />
			<frames._page_inner_content>
				<frames._grid_topBtns_outer>
					<frames._grid_topBtns_inner style={{ position: "relative", justifyContent: "end" }}>
						<div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}>
							<NewInput
								onChange={debouncedSearch}
								fieldOverrides={{ fieldStyle: NewFieldStyles.greySecondary }}
								placeholder={"search"}
								isClearable
							/>
						</div>
						<NewButton
							content={"fetch"}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								fetchData();
							}}
							disabled={loading}
						/>
					</frames._grid_topBtns_inner>
					<div style={{ gridArea: "2/1/3/2", border: "5px solid " + colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary }}>
						{loading && <LoadingSpinner />}
						{error && <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>ERROR: failed to fetch data</p>}
						{!loading && !error &&
							<ReactiveTable
								table={table}
								tableType={TableStyles_Types.primaryTable}
								emptyText="No data found"
								renderFooter={false}
							/>
						}
					</div>
				</frames._grid_topBtns_outer>
			</frames._page_inner_content>
			<div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "5px 20px 0px 20px" }}>
				<NewButton
					content={""}
					buttonType={ButtonTypes.arrowButton}
					onClick={() => {
						if (table.getCanPreviousPage()) {
							table.previousPage()
						}
					}}
					startEnhancer={glyphs.Arrow({ rotate: 180 })}
					overrides={{
						baseStyles: { backgroundColor: colors.greyPrimary },
						hoverStyles: { backgroundColor: colors.greySecondary },
					}}
					disabled={!table.getCanPreviousPage()}
				/>
				<NewButton
					content={""}
					buttonType={ButtonTypes.arrowButton}
					onClick={() => {
						if (table.getCanNextPage()) {
							table.nextPage()
						}
					}}
					startEnhancer={glyphs.Arrow({})}
					overrides={{
						baseStyles: { backgroundColor: colors.greyPrimary },
						hoverStyles: { backgroundColor: colors.greySecondary },
					}}
					disabled={!table.getCanNextPage()}
				/>
				<p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
					{`page ${table.getState().pagination.pageIndex + 1} of ${table.getPageCount()}`}
				</p>
			</div>
		</frames.Page_inner>
	)
}